/**
 * @fileoverview Motor de búsqueda fuzzy optimizado para nombres de infraestructuras PTEL
 * @module services/search/FuzzySearchEngine
 * @version 1.0.0
 * 
 * Usa uFuzzy para búsquedas tolerantes a errores tipográficos,
 * acentos y variaciones en nombres de infraestructuras.
 * 
 * Características:
 * - Pre-indexación para búsquedas O(1)
 * - Soporte nativo para español (acentos, ñ)
 * - Normalización de abreviaturas (C.E.I.P. → ceip)
 * - 10-50x más rápido que Fuse.js
 * 
 * @example
 * ```typescript
 * const engine = new FuzzySearchEngine({ mode: 'fuzzy' });
 * engine.index([
 *   { id: '1', text: 'C.E.I.P. García Lorca' },
 *   { id: '2', text: 'Centro de Salud Colomera' },
 * ]);
 * 
 * const results = engine.search('ceip garcia lorca');
 * // [{ item: { id: '1', ... }, score: 0.85, matches: [...] }]
 * ```
 */

import uFuzzy from '@leeoniya/ufuzzy';

// =====================================================
// TIPOS
// =====================================================

/**
 * Item que puede ser buscado
 */
export interface SearchableItem {
  /** Identificador único */
  id: string;
  /** Texto principal para búsqueda */
  text: string;
  /** Metadatos adicionales (coordenadas, tipología, etc.) */
  metadata?: Record<string, unknown>;
}

/**
 * Resultado de una búsqueda
 */
export interface SearchResult {
  /** Item encontrado */
  item: SearchableItem;
  /** Score de coincidencia (0-1, mayor es mejor) */
  score: number;
  /** Posiciones de los matches para highlighting [start, end][] */
  matches: Array<[number, number]>;
  /** Índice original en el haystack */
  originalIndex: number;
}

/**
 * Modo de búsqueda
 * - strict: Solo matches exactos (después de normalización)
 * - fuzzy: Tolera errores tipográficos (1 error por término)
 * - outOfOrder: Configuración más permisiva para variaciones
 */
export type SearchMode = 'strict' | 'fuzzy' | 'outOfOrder';

/**
 * Opciones de configuración del motor
 */
export interface FuzzySearchOptions {
  /** Modo de búsqueda (default: 'fuzzy') */
  mode?: SearchMode;
  /** Máximo resultados a devolver (default: 10) */
  limit?: number;
  /** Umbral mínimo de score 0-1 (default: 0.2) */
  threshold?: number;
}

/**
 * Opciones de búsqueda por query
 */
export interface SearchQueryOptions {
  /** Sobrescribe límite de resultados */
  limit?: number;
  /** Sobrescribe umbral de score */
  threshold?: number;
}

/**
 * Estadísticas del motor
 */
export interface SearchStats {
  /** Número de items indexados */
  indexedItems: number;
  /** Búsquedas realizadas desde última indexación */
  searchCount: number;
  /** Tiempo promedio de búsqueda en ms */
  avgSearchTime: number;
  /** ¿Está indexado? */
  isIndexed: boolean;
}

// =====================================================
// IMPLEMENTACIÓN
// =====================================================

/**
 * Motor de búsqueda fuzzy optimizado para infraestructuras PTEL
 * 
 * Usa uFuzzy internamente (~4KB vs 24KB de Fuse.js)
 * con configuración específica para español.
 */
export class FuzzySearchEngine {
  private uf: uFuzzy;
  private items: SearchableItem[] = [];
  private haystack: string[] = [];
  private indexed = false;
  private readonly options: Required<FuzzySearchOptions>;
  
  // Estadísticas
  private searchCount = 0;
  private totalSearchTime = 0;

  /**
   * Crea una nueva instancia del motor de búsqueda
   * 
   * @param options - Configuración del motor
   */
  constructor(options?: FuzzySearchOptions) {
    this.options = {
      mode: options?.mode ?? 'fuzzy',
      limit: options?.limit ?? 10,
      threshold: options?.threshold ?? 0.2,
    };

    // Configuración de uFuzzy optimizada para español
    this.uf = new uFuzzy({
      // Modo intra-término: 0=strict, 1=fuzzy (single-error tolerant)
      intraMode: this.options.mode === 'strict' ? 0 : 1,
      
      // Tolerancia a errores (solo aplica si intraMode=1)
      intraIns: 1,   // 1 inserción permitida
      intraSub: 1,   // 1 sustitución permitida
      intraTrn: 1,   // 1 transposición permitida
      intraDel: 1,   // 1 eliminación permitida
      
      // Margen de caracteres entre términos
      interLft: 2,
      interRgt: 0,
    });
  }

  /**
   * Indexa un array de items para búsquedas posteriores
   * 
   * IMPORTANTE: Llamar UNA VEZ al cargar datos, NO en cada búsqueda.
   * Invalida cualquier índice anterior.
   * 
   * @param items - Items a indexar
   * @returns Número de items indexados
   */
  index(items: SearchableItem[]): number {
    this.items = [...items];
    this.haystack = items.map(item => this.normalizeText(item.text));
    this.indexed = true;
    this.searchCount = 0;
    this.totalSearchTime = 0;
    
    return this.items.length;
  }

  /**
   * Añade items al índice existente sin reconstruir todo
   * 
   * @param items - Items adicionales a indexar
   * @returns Total de items indexados
   */
  addToIndex(items: SearchableItem[]): number {
    if (!this.indexed) {
      return this.index(items);
    }
    
    this.items.push(...items);
    this.haystack.push(...items.map(item => this.normalizeText(item.text)));
    
    return this.items.length;
  }

  /**
   * Búsqueda fuzzy sobre items indexados
   * 
   * @param query - Texto a buscar
   * @param options - Opciones específicas para esta búsqueda
   * @returns Array de resultados ordenados por score descendente
   * @throws Error si no se ha llamado a index() previamente
   */
  search(query: string, options?: SearchQueryOptions): SearchResult[] {
    if (!this.indexed) {
      throw new Error('FuzzySearchEngine: debe llamar index() antes de search()');
    }

    const startTime = performance.now();
    const normalizedQuery = this.normalizeText(query);
    const limit = options?.limit ?? this.options.limit;
    const threshold = options?.threshold ?? this.options.threshold;

    // Búsqueda vacía
    if (!normalizedQuery.trim()) {
      return [];
    }

    // uFuzzy search
    const searchResult = this.uf.search(this.haystack, normalizedQuery);
    
    if (!searchResult || !searchResult[0] || searchResult[0].length === 0) {
      this.updateStats(startTime);
      return [];
    }

    const [idxs, info, order] = searchResult;
    const results: SearchResult[] = [];

    // Procesar resultados en orden de relevancia
    const processOrder = order ?? idxs.map((_, i) => i);
    
    for (let i = 0; i < Math.min(processOrder.length, limit * 2); i++) {
      const orderIdx = processOrder[i];
      const haystackIdx = idxs[orderIdx];
      const item = this.items[haystackIdx];
      
      if (!item) continue;

      // Calcular score
      const score = this.calculateScore(normalizedQuery, haystackIdx, info, orderIdx);
      
      if (score >= threshold) {
        const matches = this.extractMatches(info, orderIdx);
        
        results.push({
          item,
          score,
          matches,
          originalIndex: haystackIdx,
        });
      }
      
      if (results.length >= limit) {
        break;
      }
    }

    results.sort((a, b) => b.score - a.score);
    
    this.updateStats(startTime);
    return results.slice(0, limit);
  }

  /**
   * Búsqueda rápida que solo devuelve el mejor match
   * Útil para validación cruzada de nombres
   * 
   * @param query - Texto a buscar
   * @param minThreshold - Umbral mínimo (default: 0.3)
   * @returns Mejor resultado o null si no hay match
   */
  searchBest(query: string, minThreshold = 0.3): SearchResult | null {
    const results = this.search(query, { limit: 1, threshold: minThreshold });
    return results[0] ?? null;
  }

  /**
   * Busca coincidencia exacta (después de normalización)
   * Más rápido que search() para validaciones
   * 
   * @param text - Texto a buscar exactamente
   * @returns Item encontrado o null
   */
  findExact(text: string): SearchableItem | null {
    if (!this.indexed) {
      throw new Error('FuzzySearchEngine: debe llamar index() antes de findExact()');
    }
    
    const normalized = this.normalizeText(text);
    const idx = this.haystack.indexOf(normalized);
    
    return idx >= 0 ? this.items[idx] : null;
  }

  /**
   * Compara dos textos y devuelve score de similitud
   * Sin necesidad de indexar previamente
   * 
   * @param text1 - Primer texto
   * @param text2 - Segundo texto
   * @returns Score de similitud (0-1)
   */
  comparePair(text1: string, text2: string): number {
    const norm1 = this.normalizeText(text1);
    const norm2 = this.normalizeText(text2);
    
    if (norm1 === norm2) return 1.0;
    
    const maxLen = Math.max(norm1.length, norm2.length);
    if (maxLen === 0) return 1.0;
    
    const distance = this.levenshteinDistance(norm1, norm2);
    return Math.max(0, 1 - distance / maxLen);
  }

  /**
   * Obtiene estadísticas del motor
   */
  getStats(): SearchStats {
    return {
      indexedItems: this.items.length,
      searchCount: this.searchCount,
      avgSearchTime: this.searchCount > 0 
        ? this.totalSearchTime / this.searchCount 
        : 0,
      isIndexed: this.indexed,
    };
  }

  /**
   * Limpia índice y libera memoria
   */
  clear(): void {
    this.items = [];
    this.haystack = [];
    this.indexed = false;
    this.searchCount = 0;
    this.totalSearchTime = 0;
  }

  /**
   * ¿Está el motor indexado y listo para búsquedas?
   */
  get isReady(): boolean {
    return this.indexed && this.items.length > 0;
  }

  /**
   * Número de items indexados
   */
  get size(): number {
    return this.items.length;
  }

  // =====================================================
  // MÉTODOS PRIVADOS
  // =====================================================

  /**
   * Normaliza texto para comparación
   * 
   * Transformaciones:
   * 1. Lowercase
   * 2. Elimina acentos (excepto ñ)
   * 3. Colapsa abreviaturas: C.E.I.P. → ceip, I.E.S. → ies
   * 4. Elimina caracteres especiales
   * 5. Colapsa espacios múltiples
   * 
   * @example
   * "C.E.I.P. García Lorca" → "ceip garcia lorca"
   * "I.E.S. Padre Manjón" → "ies padre manjon"
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      // Normalizar Unicode y eliminar diacríticos (excepto ñ)
      .normalize('NFD')
      .replace(/[\u0300-\u0309\u030b-\u036f]/g, '')
      // Preservar ñ (n + tilde → ñ)
      .replace(/n\u0303/g, 'ñ')
      // Colapsar abreviaturas con puntos: C.E.I.P. → ceip
      .replace(/\b([a-zñ])\.([a-zñ])\.([a-zñ])\.([a-zñ])\.?/g, '$1$2$3$4')
      .replace(/\b([a-zñ])\.([a-zñ])\.([a-zñ])\.?/g, '$1$2$3')
      .replace(/\b([a-zñ])\.([a-zñ])\.?/g, '$1$2')
      // Eliminar puntos restantes y otros caracteres especiales
      .replace(/[^a-zñ0-9\s]/g, ' ')
      // Colapsar espacios múltiples
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Calcula score normalizado 0-1
   * 
   * uFuzzy ranges format: [start1, end1, start2, end2, ...] (flat array)
   */
  private calculateScore(
    query: string,
    haystackIdx: number,
    info: unknown,
    orderIdx: number
  ): number {
    const haystackText = this.haystack[haystackIdx];
    if (!haystackText) return 0;

    // Si tenemos info de uFuzzy, usar ranges
    if (info && typeof info === 'object' && 'ranges' in info) {
      const ranges = (info as { ranges: number[][] }).ranges;
      const itemRanges = ranges?.[orderIdx];
      
      // uFuzzy ranges: flat array [start1, end1, start2, end2, ...]
      if (itemRanges && itemRanges.length >= 2) {
        let matchedChars = 0;
        for (let i = 0; i < itemRanges.length; i += 2) {
          const start = itemRanges[i];
          const end = itemRanges[i + 1];
          if (typeof start === 'number' && typeof end === 'number') {
            matchedChars += end - start;
          }
        }
        
        // Score basado en cobertura y longitud relativa
        const coverage = matchedChars / haystackText.length;
        const queryRatio = Math.min(1, query.length / haystackText.length);
        
        return Math.min(1, (coverage * 0.6 + queryRatio * 0.4));
      }
    }

    // Fallback: Levenshtein
    return this.comparePair(query, haystackText);
  }

  /**
   * Extrae posiciones de matches para highlighting
   * 
   * uFuzzy ranges format: [start1, end1, start2, end2, ...] (flat array)
   */
  private extractMatches(info: unknown, orderIdx: number): Array<[number, number]> {
    if (!info || typeof info !== 'object' || !('ranges' in info)) {
      return [];
    }

    const ranges = (info as { ranges: number[][] }).ranges;
    const itemRanges = ranges?.[orderIdx];
    
    if (!itemRanges || itemRanges.length < 2) return [];

    // Convert flat array [s1, e1, s2, e2, ...] to pairs [[s1, e1], [s2, e2], ...]
    const matches: Array<[number, number]> = [];
    for (let i = 0; i < itemRanges.length; i += 2) {
      const start = itemRanges[i];
      const end = itemRanges[i + 1];
      if (typeof start === 'number' && typeof end === 'number') {
        matches.push([start, end]);
      }
    }
    
    return matches;
  }

  /**
   * Distancia de Levenshtein
   */
  private levenshteinDistance(s1: string, s2: string): number {
    const len1 = s1.length;
    const len2 = s2.length;
    
    if (len1 === 0) return len2;
    if (len2 === 0) return len1;
    
    const row = Array.from({ length: len2 + 1 }, (_, i) => i);
    
    for (let i = 1; i <= len1; i++) {
      let prev = i;
      for (let j = 1; j <= len2; j++) {
        const curr = s1[i - 1] === s2[j - 1]
          ? row[j - 1]
          : Math.min(row[j - 1] + 1, prev + 1, row[j] + 1);
        row[j - 1] = prev;
        prev = curr;
      }
      row[len2] = prev;
    }
    
    return row[len2];
  }

  /**
   * Actualiza estadísticas
   */
  private updateStats(startTime: number): void {
    const elapsed = performance.now() - startTime;
    this.searchCount++;
    this.totalSearchTime += elapsed;
  }
}

// =====================================================
// FACTORY Y HELPERS
// =====================================================

/**
 * Crea motor con configuración para nombres de infraestructuras PTEL
 * Pre-configurado para español y tolerancia a variaciones
 */
export function createPTELSearchEngine(): FuzzySearchEngine {
  return new FuzzySearchEngine({
    mode: 'fuzzy',
    limit: 5,
    threshold: 0.25,
  });
}

/**
 * Crea motor estricto para validación exacta
 * Solo matches con alta confianza
 */
export function createStrictSearchEngine(): FuzzySearchEngine {
  return new FuzzySearchEngine({
    mode: 'strict',
    limit: 3,
    threshold: 0.7,
  });
}
